export type Nullable<T> = T | null | undefined;

export const isNull = <T>(value: T | null): value is null => value === null;

export const isUndefined = <T>(value: T | undefined): value is undefined => value === undefined;

export const isNotNullNorUndefined = <T>(value: T | undefined | null): value is T => {
  return !isNull(value) && !isUndefined(value);
};

export const isNullOrUndefined = <T>(value: T | undefined | null): value is undefined | null => {
  return !isNotNullNorUndefined(value);
};

export const mapNullable = <T, U>(value: T, mapFn: (value: NonNullable<T>) => U): Extract<T, null | undefined> | U => {
  /**
   * TODO: figure out how to avoid casting as `any`
   */
  if (isNullOrUndefined(value)) {
    return value as any;
  }

  return mapFn(value as any);
};

type MapToNonNullable<T> = { [K in keyof T]: NonNullable<T[K]> };

// Overload signatures
export function mapNullables<T extends readonly any[], U>(
  arr: [...T],
  mapFn: (...args: MapToNonNullable<[...T]>) => U
): U | null;
export function mapNullables<T extends object, U>(obj: T, mapFn: (args: MapToNonNullable<T>) => U): U | null;

// Implementation signature
export function mapNullables(nullables: unknown, mapFn: any): unknown {
  if (Array.isArray(nullables)) {
    return nullables.every(isNotNullNorUndefined) ? mapFn(...nullables) : null;
  }

  if (typeof nullables === "object" && isNotNullNorUndefined(nullables)) {
    return Object.values(nullables).every(isNotNullNorUndefined) ? mapFn(nullables) : null;
  }

  throw new Error("Invalid nullables argument");
}
